import Text from '@axa-japan/design-system-react/Text';
import { primitiveColors } from '@axa-japan/design-system-react/colors';
import { useCallback, useEffect, useRef, useState } from 'react';
import crossIconUrl from '../../assets/cross_icon.svg';
import useOrientation from '../../hooks/useOrientation';
import { useQualityCheckCache } from '../../providers/QualityCheckCacheProvider';
import { useUserContext } from '../../providers/UserProvider';
import { calculateCropRectangle, CameraInterface, createCameraInterface, DEFAULT_VIDEO_CONSTRAINTS, parseCameraError } from '../../utils/cameraUtils';
import { ImageQualityCheckResult } from '../../utils/imageQualityCheck/imageQualityCheck';
import CustomModal from '../common/CustomModal';
import OrientationWarning from '../common/OrientationWarning/OrientationWarning';
import QualityCheckPopup from './QualityCheckPopup';

// '3': 健康診断結果通知書, '4': 人間ドック成績表
const FORM_IDS_WITHOUT_GUIDE_TEXT = ['3', '4'];

type CameraProps = {
  onCapture: (file: File, src: string | null) => void;
  onClose: () => void;
  formId?: string;
};

const CAPTURE_FRAME_ASPECT = 297 / 210;
const CAPTURE_FRAME_HEIGHT_RATIO = 0.9;
const CAPTURE_FRAME_BORDER_WIDTH = 3;

const Camera = ({ onCapture, onClose, formId }: CameraProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const cameraRef = useRef<CameraInterface | null>(null);
  const deviceOrientation = useOrientation();
  const isLandscape = deviceOrientation === 'landscape';

  const { setLoader } = useUserContext();
  // Stabilize setLoader reference to prevent infinite loop in useEffect
  const setLoaderRef = useRef(setLoader);
  setLoaderRef.current = setLoader;

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [capturedImgFile, setCapturedImgFile] = useState<File>();
  const [capturedImgSrc, setCapturedImgSrc] = useState<string | null>();
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [qualityResult, setQualityResult] = useState<ImageQualityCheckResult>();
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [isQualityPopupOpen, setIsQualityPopupOpen] = useState<boolean>(true);

  const { getOrCheck } = useQualityCheckCache();

  const closeCamera = useCallback(() => {
    if (cameraRef.current) {
      cameraRef.current.close();
      cameraRef.current = null;
    }
    setIsCameraReady(false);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeCamera = async () => {
      if (!videoRef.current || !isLandscape) return;

      try {
        setLoaderRef.current(true);
        const camera = createCameraInterface();
        cameraRef.current = camera;
        await camera.initialize(videoRef.current, DEFAULT_VIDEO_CONSTRAINTS);
        if (isMounted) {
          setIsCameraReady(true);
          setLoaderRef.current(false);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(parseCameraError(`${error}`));
          setLoaderRef.current(false);
          setIsErrorModalOpen(true);
        }
      }
    };

    if (isLandscape) {
      initializeCamera();
    }

    return () => {
      isMounted = false;
      closeCamera();
    };
  }, [isLandscape, closeCamera]);

  // Draw the capture frame overlay (blurred/darkened outside a centered 3:2 cutout)
  useEffect(() => {
    if (!isCameraReady || capturedImgSrc) return undefined;

    const video = videoRef.current;
    const canvas = overlayCanvasRef.current;
    const container = videoContainerRef.current;
    if (!video || !canvas || !container) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let animationFrameId: number;

    const draw = () => {
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      if (canvas.width !== containerWidth || canvas.height !== containerHeight) {
        canvas.width = containerWidth;
        canvas.height = containerHeight;
      }

      // Wait until the video has real dimensions
      if (!video.videoWidth || !video.videoHeight) {
        animationFrameId = requestAnimationFrame(draw);
        return;
      }

      const cutoutHeight = containerHeight * CAPTURE_FRAME_HEIGHT_RATIO;
      const cutoutWidth = cutoutHeight * CAPTURE_FRAME_ASPECT;
      const cutoutX = (containerWidth - cutoutWidth) / 2;
      const cutoutY = (containerHeight - cutoutHeight) / 2;

      ctx.clearRect(0, 0, containerWidth, containerHeight);

      // Blurred copy of the current video frame — sampled from the same
      // object-cover visible rectangle the user sees, then drawn to fill the
      // overlay so the blurred backdrop lines up with the un-blurred cutout.
      // Use the container's actual aspect ratio so this works regardless of container size.
      const containerAspect = containerWidth / containerHeight;
      const { cropX, cropY, cropWidth, cropHeight } = calculateCropRectangle(video.videoWidth, video.videoHeight, containerAspect);
      ctx.save();
      ctx.filter = 'blur(4px)';
      ctx.drawImage(video, cropX, cropY, cropWidth, cropHeight, 0, 0, containerWidth, containerHeight);
      ctx.filter = 'none';
      ctx.restore();

      // Semi-transparent dark tint
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.fillRect(0, 0, containerWidth, containerHeight);

      // Punch out the centered 3:2 cutout so the live video shows through
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0, 0, 0, 1)';
      ctx.fillRect(cutoutX, cutoutY, cutoutWidth, cutoutHeight);
      ctx.restore();

      // Red border around the cutout
      ctx.strokeStyle = primitiveColors.red400;
      ctx.lineWidth = CAPTURE_FRAME_BORDER_WIDTH;
      ctx.strokeRect(cutoutX, cutoutY, cutoutWidth, cutoutHeight);

      animationFrameId = requestAnimationFrame(draw);
    };

    animationFrameId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isCameraReady, capturedImgSrc]);

  const showCaptureGuideText = !formId || !FORM_IDS_WITHOUT_GUIDE_TEXT.includes(formId);

  const capture = async () => {
    try {
      if (cameraRef.current) {
        const container = videoContainerRef.current;
        const containerAspect = container ? container.clientWidth / container.clientHeight : undefined;

        if (showCaptureGuideText) {
          // Capture twice: preview without rotation, upload with rotation
          const [preview, rotated] = await Promise.all([
            cameraRef.current.takePhoto(containerAspect, false),
            cameraRef.current.takePhoto(containerAspect, true),
          ]);
          if (preview && rotated) {
            setCapturedImgSrc(preview.dataUrl);
            setCapturedImgFile(rotated.file);
          }
        } else {
          const result = await cameraRef.current.takePhoto(containerAspect, false);
          if (result) {
            setCapturedImgSrc(result.dataUrl);
            setCapturedImgFile(result.file);
          }
        }
      }
    } catch (e) {
      setIsErrorModalOpen(true);
      setErrorMessage(`${e}`);
    }
  };

  const handleClose = useCallback(() => {
    closeCamera();
    onClose();
  }, [closeCamera, onClose]);

  const QUALITY_CHECK_IMAGE_TYPES = ['image/png', 'image/jpg', 'image/jpeg'];

  useEffect(() => {
    if (!capturedImgFile) return;
    if (!QUALITY_CHECK_IMAGE_TYPES.includes(capturedImgFile.type)) {
      setQualityResult(undefined);
      setIsChecking(false);
      return;
    }

    let cancelled = false;
    setIsChecking(true);
    setQualityResult(undefined);
    setIsQualityPopupOpen(true);

    getOrCheck(capturedImgFile)
      .then((result) => {
        if (cancelled) return;
        setQualityResult(result);
      })
      .catch(() => {
        if (cancelled) return;
        setQualityResult(undefined);
      })
      .finally(() => {
        if (cancelled) return;
        setIsChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [capturedImgFile, getOrCheck]);

  const handleRetake = useCallback(() => {
    setCapturedImgSrc(null);
    setCapturedImgFile(undefined);
    setQualityResult(undefined);
    setIsChecking(false);
  }, []);

  if (deviceOrientation === 'portrait') {
    return <OrientationWarning />;
  }

  const showOverlay = isCameraReady && !capturedImgSrc;

  return (
    <div className="m-auto w-full flex flex-col justify-between h-full bg-white">
      <CustomModal buttonLabel="OK" isWarningModal open={isErrorModalOpen} title="カメラを起動できません。" onClose={handleClose}>
        <div>{errorMessage}</div>
      </CustomModal>
      <button type="button" onClick={handleClose} className="fixed top-5 right-5 border-0 bg-transparent p-0 cursor-pointer">
        <img className="w-10" src={crossIconUrl} alt="Close" />
      </button>

      <div className="flex flex-row !w-full h-full justify-around items-center gap-4 pb-10 pt-20 px-6">
        <div ref={videoContainerRef} className="h-full w-full bg-interactiveVisitedGrey flex items-center justify-center relative overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover transition delay-150 duration-300 ease-in-out ${isCameraReady ? 'opacity-100' : 'opacity-0'}`}
            style={capturedImgSrc ? { position: 'absolute', visibility: 'hidden' } : undefined}
          />

          {showOverlay && (
            <>
              <canvas ref={overlayCanvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
              {showCaptureGuideText && (
                <>
                  <Text
                    size="md"
                    weight="bold"
                    color="primary-white"
                    className="absolute top-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none whitespace-nowrap"
                  >
                    帳票が赤い四角内に収まるように撮影してください
                  </Text>
                  <Text
                    size="md"
                    weight="bold"
                    color="primary-white"
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none"
                    style={{ writingMode: 'vertical-rl' }}
                  >
                    帳票上側
                  </Text>
                  <Text
                    size="md"
                    weight="bold"
                    color="primary-white"
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none"
                    style={{ writingMode: 'vertical-rl' }}
                  >
                    帳票下側
                  </Text>
                </>
              )}
            </>
          )}

          {capturedImgSrc && (
            <>
              <img src={capturedImgSrc} className="absolute inset-0 w-full h-full object-contain z-10" alt="Captured" />
              <Text
                size="md"
                weight="bold"
                color="primary-white"
                className="absolute top-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none whitespace-nowrap px-3 py-1 rounded"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
              >
                文字ははっきり読めますか？帳票全体が欠けなく撮影できていますか？
              </Text>
              <QualityCheckPopup
                open={isQualityPopupOpen}
                onClose={() => setIsQualityPopupOpen(false)}
                containerRef={videoContainerRef}
                isChecking={isChecking}
                result={qualityResult}
              />
            </>
          )}
        </div>
        {capturedImgSrc ? (
          <div className="h-full min-w-52 rounded flex flex-col gap-4 justify-center items-center">
            <button
              type="button"
              className="bg-interactiveActivePrimary h-1/2 w-full items-center flex justify-center border-0 p-0 cursor-pointer"
              onClick={() => onCapture(capturedImgFile as File, capturedImgSrc)}
            >
              <Text size="lg" weight="bold" color="primary-white">
                はい
              </Text>
            </button>

            <button
              type="button"
              className="bg-interactiveActivePrimary h-1/2 w-full flex justify-center items-center border-0 p-0 cursor-pointer"
              onClick={handleRetake}
            >
              <Text size="lg" weight="bold" color="primary-white">
                画像を撮り直す
              </Text>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="bg-interactiveActivePrimary h-full min-w-52 rounded flex justify-center items-center border-0 p-0 cursor-pointer"
            onClick={capture}
          >
            <Text size="lg" weight="bold" color="primary-white">
              端末カメラで撮影
            </Text>
          </button>
        )}
      </div>
    </div>
  );
};

export default Camera;
